import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { fetchTags } from "./api";
import type { Tag } from "./types";

interface TagsValue {
  /** Every tag, hidden ones included (they carry the flag). Empty until loaded. */
  tags: Tag[];
  reload: () => void;
}

const TagsContext = createContext<TagsValue | null>(null);

/**
 * Loads the tag list once and shares it with every picker and filter, the way
 * RosterProvider does for owners. Sits inside AuthProvider (it needs a session).
 * Settings reloads it after a change so a new tag is offered straight away.
 */
export function TagsProvider({ children }: { children: ReactNode }) {
  const [tags, setTags] = useState<Tag[]>([]);

  const load = () => {
    void fetchTags()
      .then((t) => setTags(Array.isArray(t) ? t : []))
      .catch(() => setTags([]));
  };

  useEffect(load, []);

  return <TagsContext.Provider value={{ tags, reload: load }}>{children}</TagsContext.Provider>;
}

export function useTags(): TagsValue {
  const ctx = useContext(TagsContext);
  if (!ctx) throw new Error("useTags must be used inside <TagsProvider>");
  return ctx;
}
